import { Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { IModelDataV3, Model } from '@df/catalog/model';
import { Url } from '@df/core/service/url';
import { RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';

@Injectable({
    providedIn: 'root'
})
/**
 * Service for social media integrations
 */
export class SocialService {
    constructor(
        private url: Url,
        private requestService: RequestService,
        private app: App
    ) {}

    /**
     * Logs in using social service
     * Sends requst to be that will return redirect
     * @param  {Status |       undefined}   status
     * @param  {string}    service
     */
    login(status: Status | undefined, service: string): void {
        if (!this.canLogin(service)) {
            if (status) {
                status.error = 'Service is not availble';
            }
            return;
        }
        this.requestService.send(status, {
            path: 'social/' + service,
            data: {
                redirect: window.location.href
            }
        });
    }

    /**
     * Checks if client can use service to log in with it
     * @param  {string}  service [description]
     * @return {boolean}         [description]
     */
    canLogin(service: string): boolean {
        return !!this.app.data.connect?.[service];
    }

    /**
     * Share on facebook
     * @param {object} model
     * @param {boolean} windowName
     */
    fbShare(model: Model | string, windowName?: string): void {
        this.share(
            600,
            500,
            'http://www.facebook.com/sharer/sharer.php?u=' + (typeof model === 'string' ? model : this.getEncodedModelUrl(model)),
            windowName
        );
    }

    /**
     * Share on facebook messenger
     * @param {object} model
     * @param {boolean} windowName
     */
    fmShare(model: Model | string, windowName?: string): void {
        this.share(
            600,
            500,
            'fb-messenger://share?link=' + (typeof model === 'string' ? model : this.getEncodedModelUrl(model)),
            windowName
        );
    }

    /**
     * Share on twitter
     * @param {object} model
     * @param {boolean} windowName
     */
    twShare(model: Model | string, windowName?: string, name?: string, title?: string): void {
        const data = typeof model === 'string' ? {} : model.data;

        if (data) {
            this.share(
                600,
                450,
                'http://twitter.com/intent/tweet?url=' +
                    (typeof model === 'string' ? model : this.getEncodedModelUrl(model)) +
                    '&text=' +
                    encodeURIComponent(name || data['name'] || title || data['title'] || ''),
                windowName
            );
        }
    }

    /**
     * Share on google plus
     * @param {object} model
     * @param {boolean} windowName
     */
    gpShare(model: Model, windowName?: string): void {
        this.share(700, 500, 'https://plus.google.com/share?url=' + this.getEncodedModelUrl(model), windowName);
    }

    /**
     * Share on pinterest
     * @param {object} model
     * @param {boolean} windowName
     */
    pinShare(model: Model<IModelDataV3> | string, windowName?: string, image?: string, description?: string, title?: string): void {
        const data = typeof model === 'string' ? { meta: undefined } : model.data;

        if (data) {
            this.share(
                630,
                270,
                'http://pinterest.com/pin/create/button/?url=' +
                    (typeof model === 'string' ? model : this.getEncodedModelUrl(model)) +
                    '&description=' +
                    encodeURIComponent(data.meta ? data.meta.description || data.meta.title || '' : description || title || '') +
                    '&media=' +
                    this.url.toMediaUrl(image || data['image']),
                windowName
            );
        }
    }

    /**
     * Share on tumblr
     * @param {object} model
     * @param {boolean} windowName
     */
    tumbShare(model: Model, windowName?: string, image?: string): void {
        const data = model.data;
        if (data) {
            this.share(630, 270, 'https://www.tumblr.com/share/link?url=' + this.getEncodedModelUrl(model), windowName);
        }
    }

    /**
     * Share on whatsapp
     * @param {object} model
     * @param {boolean} windowName
     */
    waShare(model: Model | string, windowName?: string): void {
        this.share(
            600,
            500,
            'https://api.whatsapp.com/send?text=' + (typeof model === 'string' ? model : this.getEncodedModelUrl(model)),
            windowName
        );
    }

    /**
     * Share by email.
     * @param model {Model}
     * @param email {string}
     */
    emailShare(model: Model, email?: string): string {
        return 'mailto:' + (email || '') + '?body=' + this.getEncodedModelUrl(model);
    }

    protected getEncodedModelUrl(model: Model): string {
        return encodeURIComponent(this.url.toUrl((<any>model).data.url));
    }

    /**
     * Open share popup
     */
    protected share(width: number, height: number, shareUrl: string, windowName: string = 'socialShare'): void {
        const screen = window.screen;

        const left = Math.round(screen.width / 2 - width / 2);
        const top = screen.height > height ? Math.round(screen.height / 3 - height / 2) : 0;

        const windowFeatures =
            windowName !== '_blank'
                ? 'left=' +
                  left +
                  ',top=' +
                  top +
                  ',width=' +
                  width +
                  ',height=' +
                  height +
                  ',personalbar=0,toolbar=0,scrollbars=1,resizable=1'
                : undefined;

        const popup = window.open(shareUrl, windowName, windowFeatures);

        popup ? popup.focus() : (location.href = shareUrl);
    }
}
